/**
 * Validates that a URL is safe for internal redirect.
 * Prevents open redirect attacks by only allowing same-origin relative paths.
 *
 * Open redirect = when an attacker crafts a URL like:
 *   https://cx.plivo.com/login?next_url=https://evil-phishing-site.com
 * After login, the user gets sent to the attacker's site instead of Plivo.
 *
 * Rules:
 * - Must start with / (relative path)
 * - Must NOT start with // (protocol-relative URL — browsers treat //evil.com as external)
 * - Must NOT start with javascript: or data: (XSS vectors)
 * - Must resolve to the same origin as the current page (defense-in-depth via URL parser)
 *
 * Note: We intentionally allow :// inside query params (e.g. /oauth/callback?issuer=https://idp.example.com).
 * The URL parser handles this correctly — it only cares about the scheme/authority of the URL itself.
 *
 * @param url - The URL to validate (from query params, location state, etc.)
 * @returns The URL if safe, or null if rejected
 */
export function getSafeRedirectUrl(url: string | null | undefined): string | null {
  if (!url) return null

  const trimmed = url.trim()

  // Must be a relative path starting with /
  if (!trimmed.startsWith('/')) return null

  // Protocol-relative URLs (//evil.com) are treated as external by browsers
  if (trimmed.startsWith('//')) return null

  // XSS vectors — javascript: and data: URIs can execute arbitrary code
  if (/^\/?\s*javascript:/i.test(trimmed)) return null
  if (/^\/?\s*data:/i.test(trimmed)) return null

  // Use the browser's URL parser to resolve the path against our origin.
  // This is defense-in-depth: even if an edge case slips past the string checks above,
  // the parser will catch it by comparing the resolved origin.
  // We reconstruct only pathname+search+hash to guarantee a relative result.
  try {
    const parsed = new URL(trimmed, window.location.origin)
    if (parsed.origin !== window.location.origin) return null
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return null
  }
}
